// src/i18n/config.ts
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import ICU from "i18next-icu";

// Import all translation files
import enCommon from "./locales/en/common.json";
import enAuth from "./locales/en/auth.json";
import enHome from "./locales/en/home.json";
import enRecipes from "./locales/en/recipes.json";
import enPreferences from "./locales/en/preferences.json";
import enPlanner from "./locales/en/planner.json";
import enShopping from "./locales/en/shopping.json";
import enOrders from "./locales/en/orders.json";
import enErrors from "./locales/en/errors.json";
import enAdmin from "./locales/en/admin.json";
import enProfile from "./locales/en/profile.json";
import enLegal from "./locales/en/legal.json";

import esCommon from "./locales/es/common.json";
import esAuth from "./locales/es/auth.json";
import esHome from "./locales/es/home.json";
import esRecipes from "./locales/es/recipes.json";
import esPreferences from "./locales/es/preferences.json";
import esPlanner from "./locales/es/planner.json";
import esShopping from "./locales/es/shopping.json";
import esOrders from "./locales/es/orders.json";
import esErrors from "./locales/es/errors.json";
import esAdmin from "./locales/es/admin.json";
import esProfile from "./locales/es/profile.json";
import esLegal from "./locales/es/legal.json";

import caCommon from "./locales/ca/common.json";
import caAuth from "./locales/ca/auth.json";
import caHome from "./locales/ca/home.json";
import caRecipes from "./locales/ca/recipes.json";
import caPreferences from "./locales/ca/preferences.json";
import caPlanner from "./locales/ca/planner.json";
import caShopping from "./locales/ca/shopping.json";
import caOrders from "./locales/ca/orders.json";
import caErrors from "./locales/ca/errors.json";
import caAdmin from "./locales/ca/admin.json";
import caProfile from "./locales/ca/profile.json";
import caLegal from "./locales/ca/legal.json";

export enum Language {
  EN = "en",
  ES = "es",
  CA = "ca",
}

// Auto-detect and load all namespaces (organized by feature)
const namespaces = [
  "common",
  "auth",
  "home",
  "recipes",
  "preferences",
  "planner",
  "shopping",
  "orders",
  "errors",
  "admin",
  "profile",
  "legal",
  // "assistant" is loaded on demand, only where the chat is shown: see ./assistantCopy.ts.
];

// Initialize i18n with bundled translations
i18n
  .use(LanguageDetector) // Detect user language
  .use(ICU) // ICU format for pluralization and advanced formatting
  .use(initReactI18next)
  .init({
    fallbackLng: Language.EN,
    supportedLngs: [Language.EN, Language.ES, Language.CA],
    load: "languageOnly", // Load only 'en', not 'en-US'
    ns: namespaces,
    defaultNS: "common",

    // Enable debug only in development
    debug: import.meta.env.DEV,

    // Bundled resources
    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        home: enHome,
        recipes: enRecipes,
        preferences: enPreferences,
        planner: enPlanner,
        shopping: enShopping,
        orders: enOrders,
        errors: enErrors,
        admin: enAdmin,
        profile: enProfile,
        legal: enLegal,
      },
      es: {
        common: esCommon,
        auth: esAuth,
        home: esHome,
        recipes: esRecipes,
        preferences: esPreferences,
        planner: esPlanner,
        shopping: esShopping,
        orders: esOrders,
        errors: esErrors,
        admin: esAdmin,
        profile: esProfile,
        legal: esLegal,
      },
      ca: {
        common: caCommon,
        auth: caAuth,
        home: caHome,
        recipes: caRecipes,
        preferences: caPreferences,
        planner: caPlanner,
        shopping: caShopping,
        orders: caOrders,
        errors: caErrors,
        admin: caAdmin,
        profile: caProfile,
        legal: caLegal,
      },
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    // Detection options
    detection: {
      order: ["localStorage", "cookie", "navigator"],
      caches: ["localStorage", "cookie"],
      lookupCookie: "thinkeat_language",
      lookupLocalStorage: "thinkeat_language",
      cookieMinutes: 10080, // 7 days
      cookieDomain: window.location.hostname,
    },

    // React options
    react: {
      useSuspense: false, // Changed to false since we're bundling translations
      bindI18n: "languageChanged loaded",
      bindI18nStore: "added removed",
      transEmptyNodeValue: "",
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ["br", "strong", "i", "p", "b"],
    },
  });

export default i18n;
